import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import type { BlogIndexPageData, Post } from '../../types/api';

export const BlogIndexPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState<BlogIndexPageData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const categoryParam = searchParams.get('category') || '';
    const tagParam = searchParams.get('tag') || '';
    const searchParam = searchParams.get('search') || '';
    const pageParam = parseInt(searchParams.get('page') || '1', 10);

    const [searchInput, setSearchInput] = useState(searchParam);

    useEffect(() => {
        setLoading(true);
        api.getPosts({
            category: categoryParam || undefined,
            tag: tagParam || undefined,
            search: searchParam || undefined,
            page: pageParam,
        }).then(res => {
            setData(res);
        }).catch(() => {}).finally(() => {
            setLoading(false);
        });
    }, [categoryParam, tagParam, searchParam, pageParam]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const next = new URLSearchParams(searchParams);
        if (searchInput.trim()) {
            next.set('search', searchInput.trim());
        } else {
            next.delete('search');
        }
        next.set('page', '1');
        setSearchParams(next);
    };

    const handleClearSearch = () => {
        setSearchInput('');
        const next = new URLSearchParams(searchParams);
        next.delete('search');
        next.set('page', '1');
        setSearchParams(next);
    };

    const handleCategorySelect = (slug: string | null) => {
        const next = new URLSearchParams(searchParams);
        if (slug) {
            next.set('category', slug);
        } else {
            next.delete('category');
        }
        next.set('page', '1');
        setSearchParams(next);
    };

    const handlePageChange = (p: number) => {
        const next = new URLSearchParams(searchParams);
        next.set('page', String(p));
        setSearchParams(next);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const posts = data?.posts || [];
    const categories = data?.categories || [];
    const pagination = data?.pagination;

    return (
        <div>
            {/* Header & Filters */}
            <section className="py-16 bg-radial-glow border-b border-slate-800/80">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    <div className="max-w-3xl space-y-4 mb-10">
                        <span className="text-xs font-bold text-emerald-400 tracking-wider uppercase">Perspectives & Reflections</span>
                        <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">
                            Articles & Insights
                        </h1>
                        <p className="text-base sm:text-lg text-slate-300">
                            Reflections on youth empowerment, student governance, organizational discipline, and volunteerism.
                        </p>
                    </div>

                    {/* Search & Filter Controls */}
                    <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-6 pt-4">
                        
                        {/* Category Tabs */}
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={() => handleCategorySelect(null)}
                                className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wider uppercase transition cursor-pointer ${!categoryParam ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'}`}
                            >
                                All Topics
                            </button>
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    type="button"
                                    onClick={() => handleCategorySelect(cat.slug)}
                                    className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wider uppercase transition cursor-pointer ${categoryParam === cat.slug ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'}`}
                                >
                                    {cat.name} {cat.posts_count !== undefined && <span className="opacity-60 text-[10px]">({cat.posts_count})</span>}
                                </button>
                            ))}
                        </div>

                        {/* Search Bar Form */}
                        <form onSubmit={handleSearchSubmit} className="relative max-w-sm w-full">
                            <input 
                                type="text" 
                                value={searchInput} 
                                onChange={e => setSearchInput(e.target.value)}
                                placeholder="Search articles..." 
                                className="w-full px-4 py-2.5 pl-10 rounded-full bg-slate-900 border border-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                            />
                            <div className="absolute left-3.5 top-3 text-slate-500 pointer-events-none">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            {searchParam && (
                                <button 
                                    type="button" 
                                    onClick={handleClearSearch}
                                    className="absolute right-3.5 top-2.5 text-slate-400 hover:text-white text-xs cursor-pointer"
                                >
                                    Clear
                                </button>
                            )}
                        </form>

                    </div>

                </div>
            </section>

            {/* Blog Article Grid */}
            <section className="py-20 bg-slate-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    
                    {posts.length > 0 ? (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {posts.map(post => (
                                    <article key={post.id} className="p-6 rounded-3xl glass-card glass-card-hover flex flex-col justify-between group overflow-hidden">
                                        <div className="space-y-4">
                                            
                                            {post.cover_image && (
                                                <Link to={`/blog/${post.slug}`} className="block h-44 -mx-6 -mt-6 mb-4 overflow-hidden bg-slate-900 border-b border-slate-800/80">
                                                    <img 
                                                        src={post.cover_image.startsWith('/') ? post.cover_image : `/storage/${post.cover_image}`} 
                                                        alt={post.title} 
                                                        className="w-full h-full object-cover group-hover:scale-105 transition duration-500" 
                                                        loading="lazy"
                                                    />
                                                </Link>
                                            )}

                                            <div className="flex items-center justify-between text-xs text-slate-400">
                                                <span className="font-bold text-emerald-400 uppercase tracking-wider">
                                                    {post.category?.name || 'Insight'}
                                                </span>
                                                <span>{post.reading_time_minutes} min read</span>
                                            </div>

                                            <h2 className="text-xl font-bold text-white leading-snug">
                                                <Link to={`/blog/${post.slug}`} className="hover:text-emerald-400 transition">
                                                    {post.title}
                                                </Link>
                                            </h2>

                                            <p className="text-sm text-slate-400 leading-relaxed line-clamp-3">
                                                {post.excerpt}
                                            </p>

                                            {/* Tags */}
                                            {post.tags && post.tags.length > 0 && (
                                                <div className="flex flex-wrap gap-1.5 pt-2">
                                                    {post.tags.map(tag => (
                                                        <span key={tag.id} className="px-2 py-0.5 rounded text-[11px] font-medium bg-slate-900 text-slate-400 border border-slate-800">
                                                            #{tag.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="pt-6 border-t border-slate-800/80 mt-6 flex items-center justify-between text-xs text-slate-400">
                                            <span>{post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}</span>
                                            <Link to={`/blog/${post.slug}`} className="font-bold text-emerald-400 hover:text-emerald-300 transition">
                                                Read Article &rarr;
                                            </Link>
                                        </div>
                                    </article>
                                ))}
                            </div>

                            {/* Pagination */}
                            {pagination && pagination.last_page > 1 && (
                                <div className="mt-12 flex items-center justify-center gap-2">
                                    <button
                                        type="button"
                                        disabled={pagination.current_page <= 1}
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                        className="px-4 py-2 rounded-full bg-slate-900 border border-slate-800 text-sm font-medium text-slate-300 hover:text-white disabled:opacity-40 cursor-pointer"
                                    >
                                        &larr; Previous
                                    </button>
                                    <span className="px-4 text-xs font-semibold text-slate-400">
                                        Page {pagination.current_page} of {pagination.last_page}
                                    </span>
                                    <button
                                        type="button"
                                        disabled={pagination.current_page >= pagination.last_page}
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                        className="px-4 py-2 rounded-full bg-slate-900 border border-slate-800 text-sm font-medium text-slate-300 hover:text-white disabled:opacity-40 cursor-pointer"
                                    >
                                        Next &rarr;
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-20 text-slate-400 space-y-4">
                            <p className="text-lg">No articles found matching your criteria.</p>
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchInput('');
                                    setSearchParams({});
                                }}
                                className="inline-block px-5 py-2 rounded-full bg-slate-900 border border-slate-700 text-sm font-semibold text-emerald-400 cursor-pointer"
                            >
                                Reset Filters
                            </button>
                        </div>
                    )}

                </div>
            </section>
        </div>
    );
};
